import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Card, CardContent, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions,
  ListSubheader
} from '@mui/material';
import {
  RemoveCircle as RemoveIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  ArrowBack as BackIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Restaurant as SupervisorIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types based on API response
interface InventoryItem {
  id: number;
  item_id: string;
  item_name: string;
  total_qty: number;
  display_name?: string;
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: {
    items: InventoryItem[];
  };
}

interface Staff {
  user_id: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  type: 'waiter' | 'supervisor';
}

const RemoveInventory: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  // Form states
  const [itemId, setItemId] = useState<string>('');
  const [itemName, setItemName] = useState<string>('');
  const [currentStock, setCurrentStock] = useState<number>(0);
  const [qtyRemoved, setQtyRemoved] = useState<string>('');
  const [damagedBy, setDamagedBy] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  
  // Data stores
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [waiters, setWaiters] = useState<Staff[]>([]);
  const [supervisors, setSupervisors] = useState<Staff[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  
  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    itemName: string;
    qty: number;
  }>({
    open: false,
    itemName: '',
    qty: 0
  });

  // Error state
  const [stockError, setStockError] = useState<string>('');

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch inventory items from the correct endpoint
  const fetchInventoryItems = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getInventoryItems`, 
        formData, 
        config
      );
      
      console.log("Inventory items response:", response.data);

      if (response.data.status) {
        // Access the items array from data.items
        const items = response.data.data?.items || [];
        setInventoryItems(items);
      } else {
        showNotification('Failed to fetch inventory items', 'error');
      }
    } catch (error) {
      console.error("Fetch inventory items error:", error);
      showNotification('Error fetching inventory items', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch waiters
  const fetchWaiters = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getWaiters`, formData, config);
      
      if (response.data.status) {
        const waitersData = response.data.data || [];
        const formattedWaiters = waitersData.map((waiter: any) => ({
          ...waiter,
          fullName: waiter.fullName || `${waiter.firstName || ''} ${waiter.lastName || ''}`.trim(),
          type: 'waiter' as const
        }));
        setWaiters(formattedWaiters);
      }
    } catch (error) {
      console.error("Fetch waiters error:", error);
    }
  };

  // Fetch food & beverage supervisors
  const fetchSupervisors = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/getFoodBeverageSupervisors`, formData, config);
      
      console.log("Supervisors response:", response.data);

      if (response.data.status) {
        const supervisorsData = response.data.data || [];
        const formattedSupervisors = supervisorsData.map((sup: any) => ({
          ...sup,
          fullName: sup.fullName || `${sup.firstName || ''} ${sup.lastName || ''}`.trim(),
          type: 'supervisor' as const
        }));
        setSupervisors(formattedSupervisors);
      }
    } catch (error) {
      console.error("Fetch supervisors error:", error);
    }
  };

  // Combine all staff for the dropdown
  useEffect(() => {
    const combined = [...waiters, ...supervisors];
    setAllStaff(combined);
  }, [waiters, supervisors]);

  // Handle item selection
  const handleItemChange = (event: any) => {
    const selectedId = event.target.value;
    setItemId(selectedId);
    
    const selected = inventoryItems.find(item => item.item_id === selectedId);
    if (selected) {
      setItemName(selected.item_name);
      setCurrentStock(selected.total_qty || 0);
    }
    
    // Clear any previous errors
    setStockError('');
    // Clear quantity when item changes
    setQtyRemoved('');
  };

  // Validate quantity
  const validateQuantity = (qty: number) => {
    if (currentStock === 0) {
      setStockError('Cannot remove from zero stock');
      return false;
    }
    if (qty > currentStock) {
      setStockError(`Insufficient stock. Available: ${currentStock}`);
      return false;
    }
    if (qty <= 0) {
      setStockError('Quantity must be greater than 0');
      return false;
    }
    setStockError('');
    return true;
  };

  // Handle quantity change
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQtyRemoved(value);
    
    const qty = parseInt(value);
    if (!isNaN(qty)) {
      validateQuantity(qty);
    }
  };

  // Check if item has stock
  const hasStock = (item: InventoryItem): boolean => {
    return item.total_qty > 0;
  };

  // Get items with stock for dropdown (filter out zero stock items)
  const getItemsWithStock = (): InventoryItem[] => {
    return inventoryItems.filter(item => hasStock(item));
  };

  // Get items without stock (for informational purposes)
  const getItemsWithoutStock = (): InventoryItem[] => {
    return inventoryItems.filter(item => !hasStock(item));
  };

  // Get staff name
  const getStaffName = (staff: Staff): string => {
    return staff.fullName || `${staff.firstName || ''} ${staff.lastName || ''}`.trim();
  };

  // Get staff icon based on type
  const getStaffIcon = (type: string) => {
    return type === 'waiter' ? <PersonIcon /> : <SupervisorIcon />;
  };

  // Get staff role label
  const getStaffRole = (type: string): string => {
    return type === 'waiter' ? 'Waiter' : 'F&B Supervisor';
  };

  // Open confirmation dialog
  const openConfirmDialog = () => {
    // Validation
    if (!itemId) {
      showNotification('Please select an item', 'warning');
      return;
    }

    if (currentStock === 0) {
      showNotification('Cannot remove from zero stock', 'warning');
      return;
    }

    const qty = parseInt(qtyRemoved);
    if (isNaN(qty) || !validateQuantity(qty)) {
      return;
    }

    if (!damagedBy) {
      showNotification('Please select who damaged/removed the item', 'warning');
      return;
    }

    setConfirmDialog({
      open: true,
      itemName: itemName,
      qty: qty
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    setConfirmDialog({ open: false, itemName: '', qty: 0 });
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("item_id", itemId);
      formData.append("item_name", itemName);
      formData.append("qty_removed", qtyRemoved);
      formData.append("damaged_by", damagedBy);
      formData.append("location", location);
      formData.append("notes", notes);

      const response = await axios.post(`${baseUrl()}BeverageSupervisors/removeInventory`, formData, config);
      
      if (response.data.status) {
        showNotification('Inventory removed successfully', 'success');
        resetForm();
        // Refresh inventory items to show updated stock
        await fetchInventoryItems();
      } else {
        showNotification(response.data.message || 'Failed to remove inventory', 'error');
      }
    } catch (error: any) {
      console.error("Remove inventory error:", error);
      showNotification(error.response?.data?.message || 'Error removing inventory', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setItemId('');
    setItemName('');
    setCurrentStock(0);
    setQtyRemoved('');
    setDamagedBy('');
    setLocation('');
    setNotes('');
    setStockError('');
  };

  useEffect(() => {
    fetchInventoryItems();
    fetchWaiters();
    fetchSupervisors();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const itemsWithStock = getItemsWithStock();
  const itemsWithoutStock = getItemsWithoutStock();

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
          
          {/* Header */}
          <Paper 
            elevation={0}
            sx={{ 
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()}>
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main
                  }}
                >
                  <RemoveIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Remove Inventory
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Remove damaged or used items from inventory
                  </Typography>
                </Box>
                <Tooltip title="Refresh Items">
                  <IconButton onClick={fetchInventoryItems} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="lg" sx={{ py: 3 }}>
            
            {/* Main Form */}
            <Paper sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Remove Stock
              </Typography>
              <Divider sx={{ mb: 4 }} />

              <Grid container spacing={3}>
                
                {/* Item Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Item</InputLabel>
                    <Select
                      value={itemId}
                      onChange={handleItemChange}
                      label="Select Item"
                      renderValue={(selected) => {
                        const item = inventoryItems.find(i => i.item_id === selected);
                        return item ? `${item.item_name} (Stock: ${item.total_qty})` : '';
                      }}
                    >
                      {/* Items with stock */}
                      {itemsWithStock.length > 0 && (
                        <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold', bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <InventoryIcon fontSize="small" color="success" />
                            <span>Available Items ({itemsWithStock.length})</span>
                          </Stack>
                        </MenuItem>
                      )}
                      {itemsWithStock.map(item => (
                        <MenuItem key={item.item_id} value={item.item_id}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                            <span>{item.item_name}</span>
                            <Chip 
                              label={`Stock: ${item.total_qty}`}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ ml: 2 }}
                            />
                          </Stack>
                        </MenuItem>
                      ))}

                      {/* Items without stock */}
                      {itemsWithoutStock.length > 0 && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold', bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <ErrorIcon fontSize="small" color="error" />
                              <span>Out of Stock ({itemsWithoutStock.length})</span>
                            </Stack>
                          </MenuItem>
                          {itemsWithoutStock.map(item => (
                            <MenuItem key={item.item_id} disabled sx={{ opacity: 0.7 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                                <span>{item.item_name}</span>
                                <Chip 
                                  label="Out of Stock"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ ml: 2 }}
                                />
                              </Stack>
                            </MenuItem>
                          ))}
                        </>
                      )}

                      {inventoryItems.length === 0 && (
                        <MenuItem disabled>No items available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Current Stock Display */}
                {itemId && currentStock > 0 && (
                  <Grid item xs={12} md={6}>
                    <Alert severity="info" icon={<InventoryIcon />}>
                      Current Stock: <strong>{currentStock}</strong> units available
                    </Alert>
                  </Grid>
                )}

                {itemId && currentStock === 0 && (
                  <Grid item xs={12} md={6}>
                    <Alert severity="error" icon={<ErrorIcon />}>
                      This item is out of stock. Cannot remove.
                    </Alert>
                  </Grid>
                )}

                {/* Quantity */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Quantity to Remove"
                    type="number"
                    value={qtyRemoved}
                    onChange={handleQuantityChange}
                    InputProps={{ inputProps: { min: 1, max: currentStock || 1 } }}
                    placeholder="Enter quantity"
                    error={!!stockError}
                    helperText={stockError}
                    disabled={!itemId || currentStock === 0}
                  />
                </Grid>

                {/* Damaged/Removed By - Combined Dropdown with Waiters and Supervisors */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Damaged/Removed By</InputLabel>
                    <Select
                      value={damagedBy}
                      onChange={(e) => setDamagedBy(e.target.value)}
                      label="Damaged/Removed By"
                      disabled={!itemId || currentStock === 0}
                      renderValue={(selected) => {
                        const staff = allStaff.find(s => s.user_id === selected);
                        return staff ? (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: staff.type === 'waiter' ? theme.palette.info.main : theme.palette.success.main }}>
                              {staff.type === 'waiter' ? <PersonIcon fontSize="small" /> : <SupervisorIcon fontSize="small" />}
                            </Avatar>
                            <span>{getStaffName(staff)}</span>
                            <Chip 
                              label={getStaffRole(staff.type)}
                              size="small"
                              sx={{ 
                                ml: 1,
                                bgcolor: staff.type === 'waiter' ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                                color: staff.type === 'waiter' ? theme.palette.info.main : theme.palette.success.main,
                                fontSize: '0.7rem',
                                height: 20
                              }}
                            />
                          </Stack>
                        ) : '';
                      }}
                    >
                      {/* Waiters Section */}
                      {waiters.length > 0 && [
                        <ListSubheader key="waiters-header" sx={{ bgcolor: 'transparent', fontWeight: 600, color: theme.palette.info.main }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <PersonIcon fontSize="small" />
                            <span>WAITERS</span>
                          </Stack>
                        </ListSubheader>,
                        ...waiters.map(waiter => (
                          <MenuItem key={waiter.user_id} value={waiter.user_id}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.info.main }}>
                                <PersonIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                  {getStaffName(waiter)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Waiter
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        )),
                        <Divider key="waiters-divider" sx={{ my: 1 }} />
                      ]}

                      {/* Supervisors Section */}
                      {supervisors.length > 0 && [
                        <ListSubheader key="supervisors-header" sx={{ bgcolor: 'transparent', fontWeight: 600, color: theme.palette.success.main }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <SupervisorIcon fontSize="small" />
                            <span>FOOD & BEVERAGE SUPERVISORS</span>
                          </Stack>
                        </ListSubheader>,
                        ...supervisors.map(supervisor => (
                          <MenuItem key={supervisor.user_id} value={supervisor.user_id}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.success.main }}>
                                <SupervisorIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1">
                                  {getStaffName(supervisor)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  F&B Supervisor
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        ))
                      ]}

                      {allStaff.length === 0 && (
                        <MenuItem disabled>No staff available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Location */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Main Bar, Store Room"
                    disabled={!itemId || currentStock === 0}
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                    }}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reason/Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Why is this being removed?"
                    disabled={!itemId || currentStock === 0}
                    InputProps={{
                      startAdornment: <NotesIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                    }}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button variant="outlined" onClick={resetForm}>
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<RemoveIcon />}
                      onClick={openConfirmDialog}
                      disabled={
                        submitting || 
                        !itemId || 
                        !qtyRemoved || 
                        !damagedBy || 
                        !!stockError ||
                        currentStock === 0
                      }
                      size="large"
                    >
                      Remove Stock
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Warning Card */}
            <Paper sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <WarningIcon color="warning" />
                <Typography variant="body2" color="text.secondary">
                  Removing inventory will permanently deduct from stock. 
                  This action cannot be undone. Please verify quantities before confirming.
                </Typography>
              </Stack>
            </Paper>

            {/* Summary Card */}
            <Paper sx={{ mt: 2, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Items with stock: <strong>{itemsWithStock.length}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Out of stock items: <strong>{itemsWithoutStock.length}</strong>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Container>

          {/* Confirmation Dialog */}
          <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, itemName: '', qty: 0 })}>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <WarningIcon color="warning" />
                <Typography variant="h6">Confirm Removal</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to remove <strong>{confirmDialog.qty}</strong> units of{' '}
                <strong>{confirmDialog.itemName}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This action cannot be undone. Stock will be permanently deducted.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialog({ open: false, itemName: '', qty: 0 })}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                onClick={handleSubmit}
                autoFocus
              >
                Confirm Removal
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Loading Overlay */}
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: theme.zIndex.drawer + 1,
              backdropFilter: 'blur(3px)'
            }}
            open={submitting || loading}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress color="inherit" />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {loading ? 'Loading inventory...' : 'Removing inventory...'}
              </Typography>
            </Stack>
          </Backdrop>

        </Box>
      </IonContent>
    </IonPage>
  );
};

export default RemoveInventory;